import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { isArray, isUndefined } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { Button } from "../../../../../../Common/Button";
import { FieldWrapper } from "../../../../../../DocumentUploads/Forms/GeneratedDocuments/FormFields/__styles__/FormFields";
import { Label } from "../../../../../../Inputs";
import { MultiselectBubble } from "../../../../../../Inputs/MultiselectBubble";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import { SubmissionsBuilderFieldSettings } from "../../types";
import {
  InputFieldPath,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { HelpText, WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import OptionsInput from "./OptionsInput";
import {
  DefaultValuesSection,
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
} from "./shared";

const TagsWidgetSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings) => {
  const { setValue, watch } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  let options = watch(`${fieldPath}.enum`);

  const [dropdownOptions, setDropdownOptions] = useState<string[]>(
    options?.length ?
      options.filter((value): value is string => {
        // json schema ensures that enum is an array of strings
        return typeof value === "string";
      })
    : [""],
  );

  const setDropdownOption = (index: number, value: string) => {
    const updatedOptions = [...dropdownOptions];
    updatedOptions[index] = value;
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  const addDropdownOption = () => {
    const updatedOptions = [...dropdownOptions, ""];
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  const removeDropdownOption = (index: number) => {
    dropdownOptions.splice(index, 1);
    // creating a shallow copy of the array since splice modifies in place
    const updatedOptions = [...dropdownOptions];
    setDropdownOptions(updatedOptions);
    setValue(`${fieldPath}.enum`, updatedOptions, { shouldValidate: true });
  };

  const checkBoxChanged = (evt: ChangeEvent<HTMLInputElement>) => {
    if (evt.target.checked) {
      setValue(`${fieldPath}.minItems`, 1, { shouldValidate: true });
    } else {
      setValue(`${fieldPath}.minItems`, 0, { shouldValidate: true });
    }
    setValue(`${fieldPath}.required`, evt.target.checked, {
      shouldValidate: true,
    });
  };

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={false}
        onRequiredChange={checkBoxChanged}
        readOnly={readOnly}
      />
      <div data-testid="pill-options-wrapper">
        <div>
          <Label
            style={{ marginBottom: "4px" }}
            required
            text={"Pill options"}
          />
          <HelpText style={{ marginTop: "0px", paddingBottom: "8px" }}>
            Provide options for users to select from; enter each option on a new
            field
          </HelpText>
        </div>
        <OptionsInput
          dropdownOptions={dropdownOptions}
          setDropdownOption={setDropdownOption}
          removeDropdownOption={removeDropdownOption}
          disabled={isModuleInput || readOnly}
        />
        <div style={{ paddingTop: "8px" }}>
          <Button
            styleVariant="outlineLight"
            size="small"
            leftIconName="plus"
            onClick={addDropdownOption}
            disabled={isModuleInput || readOnly}
          >
            Add option
          </Button>
        </div>
      </div>
      <DefaultValuesSection>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <TagsStaticDefaultInput
            fieldPath={fieldPath}
            readOnly={readOnly}
            dropdownOptions={dropdownOptions}
          />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

export const TagsStaticDefaultInput = ({
  fieldPath,
  readOnly,
  dropdownOptions,
}: {
  fieldPath: InputFieldPath;
  readOnly: boolean;
  dropdownOptions: Array<string>;
}) => {
  const { control, watch, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  const defaultValue = watch(`${fieldPath}.defaultValue`);

  const value = useMemo<Array<string>>(() => {
    if (isArray(defaultValue) || isUndefined(defaultValue)) {
      return defaultValue ?? [];
    }
    throw new Error(`defaultValue must be an array of strings or undefined`);
  }, [defaultValue]);

  const valueString = JSON.stringify(value);

  // filter out empty options, which would otherwise show up as an empty pill
  const options = useMemo(
    () => dropdownOptions?.filter(option => !!option),
    [dropdownOptions],
  );

  useEffect(() => {
    // handle case where a selected dropdown option is removed
    const optionsSet = new Set(options);
    const value = JSON.parse(valueString)?.filter((option: string) =>
      optionsSet.has(option),
    );

    setValue(`${fieldPath}.defaultValue`, value?.length ? value : undefined);
    // use stringified valueString value to avoid freezing up UI in infinite rerender loop
  }, [valueString, options, fieldPath, setValue]);

  return (
    <Controller
      control={control}
      name={`${fieldPath}.defaultValue`}
      render={({ field }) => {
        return (
          <MultiselectBubble
            items={options}
            selectedItems={value}
            onChange={(value: Array<string>) => {
              // store no value (undefined) for ui:defaultValue instead of empty string in form state
              field.onChange(value?.length ? value : undefined);
            }}
            name={`${fieldPath}.defaultValue`}
            label="Static default value"
            disabled={readOnly}
            readonly={readOnly}
          />
        );
      }}
    />
  );
};

TagsWidgetSettings.displayName = "TagsWidgetSettings";

export default TagsWidgetSettings;
